import { Flex, HStack, Stack, Text } from '@chakra-ui/react';

import type { Category } from '@/client/types/Category';
import CircleEditButton from '@/client/components/shared/buttons/CircleEditButton';
import type { ExpertFormData } from './types';
import type { ExpertSegmentationLineage } from '@/client/services/api/graphql/gql/graphql';
import SegmentationSearch from '@/client/components/admin/segmentation/SegmentationSearch';
import SegmentationTag from '@/client/components/admin/segmentation/SegmentationTag';
import { SegmentationTypeWithGroup } from '@/client/services/api/graphql/gql/graphql';
import { contentTypeIconMap } from '@/client/utils/type-maps/contentTypeIconMap';
import { formatSegmentationLineageName } from '@/client/utils/segmentation/formatSegmentationLineageName';
import { useExpertsStore } from '../expertsStore';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default function ExpertSegmentationForm() {
  const { t } = useTranslation();
  const { isEditingExpert: isEditing, setIsEditingExpert } = useExpertsStore();
  const { watch, setValue } = useFormContext<ExpertFormData>();
  const { segmentation } = watch();

  const segmentationTypeMap: Record<string, string> = {
    location: SegmentationTypeWithGroup.Location.toLowerCase(),
    department: SegmentationTypeWithGroup.Department.toLowerCase(),
    group: SegmentationTypeWithGroup.Group.toLowerCase(),
    team: SegmentationTypeWithGroup.Team.toLowerCase(),
    grade: SegmentationTypeWithGroup.Grade.toLowerCase(),
    org_level: SegmentationTypeWithGroup.OrgLevel.toLowerCase(),
  };

  const handleSelectedItemChange = (selectedItem: Category) => {
    const formattedSelectedItem = {
      segmentationId: selectedItem._id,
      segmentationName: selectedItem.legacyName || selectedItem.name,
      segmentationType: segmentationTypeMap[selectedItem.type],
      parent: null,
      __typename: 'NotExpertSegmentationLineage',
    } as unknown as ExpertSegmentationLineage;

    const existingMatch = segmentation.find(
      (item) => item.segmentationId === formattedSelectedItem.segmentationId,
    );

    if (existingMatch) {
      setValue(
        'segmentation',
        segmentation.filter((item) => item.segmentationId !== formattedSelectedItem.segmentationId),
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    } else {
      setValue('segmentation', [...segmentation, formattedSelectedItem], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  };

  const handleCheckIsSelected = (selectedItem: Category) =>
    !!segmentation.find((item) => item.segmentationId === selectedItem._id);

  return (
    <Stack spacing={4}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text variant="createHeading">{t('admin.aiAgent.experts.form.segmentation.title')}</Text>

        {!isEditing && (
          <CircleEditButton colorScheme="neutral" onClick={() => setIsEditingExpert(true)} />
        )}
      </Flex>

      <Text fontSize="14px" fontWeight={500} color="neutral.600">
        {t('admin.aiAgent.experts.form.segmentation.helpText')}
      </Text>

      {isEditing && (
        <SegmentationSearch
          handleSelectedItemChangeFn={handleSelectedItemChange}
          handleCheckIsSelectedFn={handleCheckIsSelected}
        />
      )}

      {segmentation.length === 0 && (
        <Text fontSize="14px" fontWeight={500} color="baseBlack">
          {t('admin.aiAgent.experts.form.segmentation.noSegmentation')}
        </Text>
      )}

      {segmentation.length > 0 && (
        <HStack flexWrap="wrap" gap={2}>
          {segmentation.map((item) => (
            <SegmentationTag
              key={item.segmentationId}
              name={
                item.__typename === 'ExpertSegmentationLineage'
                  ? formatSegmentationLineageName(item)
                  : item.segmentationName
              }
              icon={contentTypeIconMap[item.segmentationType.toLowerCase()]}
              renderCloseButton={isEditing}
              onRemove={() =>
                setValue(
                  'segmentation',
                  segmentation.filter((i) => i.segmentationId !== item.segmentationId),
                  { shouldDirty: true, shouldTouch: true, shouldValidate: true },
                )
              }
            />
          ))}
        </HStack>
      )}
    </Stack>
  );
}
